import { create } from 'zustand';
import { LoginCredentials } from 'types/index';
import {User} from 'types/user';
import { authService } from 'services/auth.service';
import { redirect } from 'react-router-dom';
import { tokenStorage } from 'utils/storage';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  changePassword: (id: string,password: string) => Promise<any>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  clearError: () => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const { user, tokens } = await authService.login(credentials);
      // Check if we have a valid access token to determine authentication
      // Only check the stored token, not the returned tokens object
      const accessToken = tokenStorage.getAccessToken();
      const hasValidToken = !!(accessToken && accessToken.trim() !== "");
      
      set({
        user,
        isAuthenticated: hasValidToken,
        isLoading: false,
        error: null,
      });
      return user;
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        isLoading: false,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  forgotPassword: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.forgotPassword(email);
      return response;
    } catch (error: any) {
      set({
        error: error.message || 'Failed to send password reset link',
        isLoading: false,
      });
      throw error;
    }finally{
      set({ isLoading: false });
    }
  },

  changePassword: async (id: string,password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.changePassword(id,password);
      return response;
    } catch (error: any) {
      set({
        error: error.message || 'Failed to send password reset link',
        isLoading: false,
      });
      throw error;
    }finally{
      set({ isLoading: false });
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      authService.clearSession();
      sessionStorage.setItem('__manual_logout__', 'true');
    }
  },

  updateUser: (user: User) => {
    set({ user });
  },

  clearError: () => {
    set({ error: null });
  },

  initializeAuth: () => {
    set({ isLoading: true });
    const isAuthenticated = authService.isAuthenticated();
    const user = authService.getCurrentUser();
    set({
      user,
      isAuthenticated,
      isLoading: false,
    });
  },
}));

